import { HttpErrorResponse } from '@angular/common/http';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { bexioActions } from '../actions/bexio.actions';
import { BexioBusinessYear, BexioConnectionStatusDto, BexioImportResult } from '../services/bexio.service';

export const BEXIO_FEATURE_KEY = 'bexio';

export interface BexioState {
    connection: BexioConnectionStatusDto | null;
    connectionStatus: 'idle' | 'loading' | 'connected' | 'expired' | 'error';
    businessYears: BexioBusinessYear[];
    businessYearsLoading: boolean;
    importStatus: 'idle' | 'loading' | 'success' | 'error';
    importResult: BexioImportResult | null;
    importError: HttpErrorResponse | null;
}

const initialState: BexioState = {
    connection: null,
    connectionStatus: 'idle',
    businessYears: [],
    businessYearsLoading: false,
    importStatus: 'idle',
    importResult: null,
    importError: null,
};

export const bexioReducer = createReducer(
    initialState,

    on(bexioActions.loadConnection, (state) => ({
        ...state,
        connectionStatus: 'loading' as const,
    })),
    on(bexioActions.loadConnectionSuccess, (state, { connection }) => ({
        ...state,
        connection,
        connectionStatus: (connection?.status ?? 'idle') as BexioState['connectionStatus'],
    })),
    on(bexioActions.loadConnectionFailure, (state) => ({
        ...state,
        connectionStatus: 'error' as const,
    })),

    on(bexioActions.loadBusinessYears, (state) => ({
        ...state,
        businessYearsLoading: true,
    })),
    on(bexioActions.loadBusinessYearsSuccess, (state, { businessYears }) => ({
        ...state,
        businessYears,
        businessYearsLoading: false,
    })),
    on(bexioActions.loadBusinessYearsFailure, (state) => ({
        ...state,
        businessYearsLoading: false,
    })),

    on(bexioActions.connectSuccess, (state) => ({
        ...state,
        connectionStatus: 'loading' as const,
    })),
    on(bexioActions.connectFailure, (state) => ({
        ...state,
        connectionStatus: 'error' as const,
    })),
    on(bexioActions.disconnectSuccess, (state) => ({
        ...state,
        connection: null,
        connectionStatus: 'idle' as const,
        businessYears: [],
    })),
    on(bexioActions.disconnectFailure, (state) => ({
        ...state,
    })),

    on(bexioActions.importData, (state) => ({
        ...state,
        importStatus: 'loading' as const,
        importResult: null,
        importError: null,
    })),
    on(bexioActions.importDataSuccess, (state, { result }) => ({
        ...state,
        importStatus: 'success' as const,
        importResult: result,
    })),
    on(bexioActions.importDataFailure, (state, { error }) => ({
        ...state,
        importStatus: 'error' as const,
        importError: error,
    })),

    on(bexioActions.resetImport, (state) => ({
        ...state,
        importStatus: 'idle' as const,
        importResult: null,
        importError: null,
    })),
);

// Selectors
const selectBexioFeature = createFeatureSelector<BexioState>(BEXIO_FEATURE_KEY);

export const selectBexioConnection = createSelector(selectBexioFeature, (s) => s.connection);
export const selectBexioConnectionStatus = createSelector(selectBexioFeature, (s) => s.connectionStatus);
export const selectBexioBusinessYears = createSelector(selectBexioFeature, (s) => s.businessYears);
export const selectBexioBusinessYearsLoading = createSelector(selectBexioFeature, (s) => s.businessYearsLoading);
export const selectBexioImportStatus = createSelector(selectBexioFeature, (s) => s.importStatus);
export const selectBexioImportResult = createSelector(selectBexioFeature, (s) => s.importResult);
export const selectBexioImportError = createSelector(selectBexioFeature, (s) => s.importError);
